<template>
  <main class="app-content">
    <div class="app-title">
      <div>
        <h1><font-awesome-icon icon="envelope"/> {{ $t('emailHistory') }}</h1>
        <p>{{ $t('emailHistoryDesc') }}</p>
      </div>
    </div>

    <div class="row">
      <!-- Tabs direction -->
      <div class="col-12">
        <b-tabs v-model="dirTabIndex" @input="onDirTab">
          <b-tab :title="$t('allEmails')"></b-tab>
          <b-tab>
            <template #title>
              <span class="log-dot" style="background: #1976d2"></span> {{ $t('inboundEmail') }}
            </template>
          </b-tab>
          <b-tab>
            <template #title>
              <span class="log-dot" style="background: #6c757d"></span> {{ $t('outboundEmail') }}
            </template>
          </b-tab>
        </b-tabs>
      </div>

      <!-- Filter -->
      <div class="col-12">
        <div class="tile">
          <div class="form-inline">
            <client-select :model="filterFormModel" class="mr-1 mb-1" attribute="client" @change="search"
                           :isFilter="true" :place-holder="$t('selectClient')"></client-select>
            <form-select :model="filterFormModel" attribute="status" class="mr-1 mb-1" :options="[
              {text: $t('allStatuses'), value: null},
              {text: $t('success'), value: 'success'},
              {text: $t('failed'), value: 'failed'},
            ]"></form-select>
            <time-range-picker :model="filterFormModel" :label="$t('time')" class="mr-1 mb-1" :defaultShortcut="3"></time-range-picker>
            <button @click="load" class="btn btn-primary mr-1 mb-1">{{ $t('search') }}</button>
            <button @click="reset" class="btn btn-secondary mr-1 mb-1">Reset</button>
            <history-export class="ml-auto mb-1"
                            v-if="['owner','admin','tech','operationsManager','account'].includes(userRole)"
                            :filter="filterFormModel" :total-count="itemCount"/>
          </div>
        </div>
      </div>

      <!-- Paging top -->
      <div class="col-12">
        <div class="c-paging clearfix">
          <div class="c-paging__count" v-if="itemCount">{{ $t('found', [itemCount]) }}</div>
          <div class="c-paging__right">
            <b-pagination v-if="itemCount > filterFormModel.pageSize" class="pull-right" size="sm"
                          v-model="filterFormModel.page" :total-rows="itemCount" :per-page="filterFormModel.pageSize"
                          @input="load"></b-pagination>
          </div>
        </div>
      </div>

      <!-- Card list -->
      <div class="col-12" v-if="items.length">
        <div class="log-list">
          <div v-for="item in items" :key="item._id"
               class="log-card" :class="{ 'log-card--error': item.sendError }"
               :style="{ borderLeftColor: item.direction === 'inbound' ? '#1976d2' : '#6c757d' }"
               @click="showEmail(item)" role="button">
            <div class="log-card__time">
              <div class="log-time-main">{{ item.createTime | moment('DD/MM HH:mm') }}</div>
              <div class="log-meta" v-if="item.sendTime">Gửi: {{ item.sendTime | moment('HH:mm') }}</div>
              <div class="log-meta" v-if="item.openTime">Mở: {{ item.openTime | moment('DD/MM HH:mm') }}</div>
            </div>

            <div class="log-card__type">
              <span class="log-type-pill"
                    :style="{ background: item.direction === 'inbound' ? '#1976d2' : '#6c757d' }">
                {{ item.direction === 'inbound' ? $t('inboundEmail') : $t('outboundEmail') }}
              </span>
              <div class="log-meta mt-1">{{ $t(`${item.type}Type`) }}</div>
            </div>

            <div class="log-card__main">
              <div class="log-row-1"><strong>{{ item.subject || '(không tiêu đề)' }}</strong></div>
              <div class="log-row-2">
                <span class="log-meta mr-1">Từ:</span>
                <span>{{ emailFrom(item) }}</span>
                <span class="log-meta mx-2">→</span>
                <span>{{ emailTo(item) }}</span>
              </div>
              <div class="log-row-3 text-danger" v-if="item.sendErrorMsg">
                <font-awesome-icon icon="exclamation-triangle" class="mr-1"/>
                {{ item.sendErrorMsg }}
              </div>
              <div class="log-row-3" v-if="item.client">
                <font-awesome-icon icon="building" class="text-muted mr-1"/>
                <span class="log-meta">{{ item.client.domain }}</span>
              </div>
            </div>

            <div class="log-card__status">
              <b-badge v-if="item.sendError" variant="danger">{{ $t('failed') }}</b-badge>
              <b-badge v-else variant="success">{{ $t('success') }}</b-badge>
            </div>

            <div class="log-card__chev"><font-awesome-icon icon="chevron-right"/></div>
          </div>
        </div>
      </div>

      <div class="col-12 text-center text-muted py-4" v-if="!items.length">
        <font-awesome-icon icon="inbox" size="3x" class="mb-2 d-block"/>
        {{ $t('notFoundSearch') }}
      </div>

      <div class="col-12" v-if="itemCount > filterFormModel.pageSize">
        <div class="c-paging clearfix">
          <div class="c-paging__count">{{ $t('found', [itemCount]) }}</div>
          <div class="c-paging__right">
            <b-pagination class="pull-right" size="sm"
                          v-model="filterFormModel.page" :total-rows="itemCount" :per-page="filterFormModel.pageSize"
                          @input="load"></b-pagination>
          </div>
        </div>
      </div>
    </div>

    <!-- Detail modal -->
    <b-modal size="xl" v-model="showForm" hide-footer scrollable :modal-class="['log-modal']">
      <template #modal-title>
        <span v-if="email && email._id">
          <span class="log-type-pill"
                :style="{ background: email.direction === 'inbound' ? '#1976d2' : '#6c757d' }">
            {{ email.direction === 'inbound' ? $t('inboundEmail') : $t('outboundEmail') }}
          </span>
          <span class="ml-2">{{ $t('emailDetail') }}</span>
        </span>
      </template>

      <div v-if="email && email._id">
        <div class="log-meta-grid">
          <div class="log-meta-cell">
            <div class="log-meta-label">{{ $t('emailType') }}</div>
            <div>{{ $t(`${email.type}Type`) }}</div>
          </div>
          <div class="log-meta-cell">
            <div class="log-meta-label">{{ $t('createTime') }}</div>
            <div>{{ email.createTime | moment('DD/MM/YYYY HH:mm:ss') }}</div>
          </div>
          <div class="log-meta-cell" v-if="email.sendTime">
            <div class="log-meta-label">{{ $t('sendTime') }}</div>
            <div>{{ email.sendTime | moment('DD/MM/YYYY HH:mm:ss') }}</div>
          </div>
          <div class="log-meta-cell" v-if="email.openTime">
            <div class="log-meta-label">{{ $t('openTime') }}</div>
            <div>{{ email.openTime | moment('DD/MM/YYYY HH:mm:ss') }}</div>
          </div>
        </div>

        <div class="log-meta-grid mt-2">
          <div class="log-meta-cell log-meta-cell--wide">
            <div class="log-meta-label">{{ $t('fromEmail') }}</div>
            <div>{{ emailFrom(email) }}</div>
          </div>
          <div class="log-meta-cell log-meta-cell--wide">
            <div class="log-meta-label">{{ $t('toEmail') }}</div>
            <div>{{ emailTo(email) }}</div>
          </div>
        </div>

        <div class="log-meta-grid mt-2" v-if="email.ccEmail && email.ccEmail.length">
          <div class="log-meta-cell" style="grid-column: span 4">
            <div class="log-meta-label">CC</div>
            <div>{{ email.ccEmail.join(', ') }}</div>
          </div>
        </div>

        <div class="alert alert-danger mt-3" v-if="email.sendErrorMsg">
          <strong>{{ $t('error') }}:</strong> {{ email.sendErrorMsg }}
        </div>

        <div class="log-section">
          <div class="log-section__label log-section__label--big">{{ $t('emailSubject') }}</div>
          <div class="log-content-block"><strong>{{ email.subject }}</strong></div>
        </div>

        <div class="log-section">
          <div class="log-section__label log-section__label--big">{{ $t('content') }}</div>
          <div class="log-content-block log-html-content" v-html="email.content"></div>
        </div>
      </div>
    </b-modal>
  </main>
</template>

<script>
import list from '../../mixins/List';
import utils from '../../utils';
import ClientSelect from "@/components/ClientSelect";
import TimeRangePicker from "../../components/TimeRangePicker";
import _ from 'lodash';

export default {
  components: { TimeRangePicker, ClientSelect },
  mixins: [list, utils],
  data() {
    return {
      userRole: this.$store.state.user.role,
      service: '/email',
      autoLoad: true,
      showForm: false,
      email: {},
      dirTabIndex: 0,
      defaultFilterFormModel: {
        client: null,
        brandName: null,
        direction: null,
        status: null
      }
    }
  },
  methods: {
    search: _.debounce(function () {
      this.filterFormModel.page = 1;
      this.load();
    }, 200),
    onDirTab(idx) {
      this.filterFormModel.direction = idx === 0 ? null : idx; // 1=inbound, 2=outbound
      this.filterFormModel.page = 1;
      this.load();
    },
    showEmail(email) {
      this.showForm = true;
      this.email = email;
    },
    emailFrom(e) {
      if (e.direction === 'inbound' && e.fromCustomer) return e.fromCustomer.name + ' <' + (e.fromEmail || '') + '>';
      if (e.direction === 'outbound' && e.user) return e.user.name + ' <' + (e.fromEmail || '') + '>';
      return e.fromEmail || '—';
    },
    emailTo(e) {
      if (e.toCustomer) return e.toCustomer.name + ' <' + (e.toEmail || '') + '>';
      if (e.toUser) return e.toUser.name + ' <' + (e.toEmail || '') + '>';
      return e.toEmail || '—';
    }
  },
  i18n: {
    messages: {
      en: {
        emailHistory: 'Email history',
        emailHistoryDesc: 'Inbound and outbound email log.',
        content: 'Content',
        fromEmail: 'From',
        toEmail: 'To',
        createTime: 'Created',
        sendTime: 'Sent',
        openTime: 'Opened',
        emailType: 'Type',
        userType: 'Personal',
        massType: 'Marketing',
        emailSubject: 'Subject',
        emailDetail: 'Email detail',
        allEmails: 'All',
        inboundEmail: 'Inbound',
        outboundEmail: 'Outbound',
        allStatuses: 'All statuses',
        failed: 'Failed',
      },
      vi: {
        emailHistory: 'Lịch sử email',
        emailHistoryDesc: 'Nhật ký email gửi đến và gửi đi.',
        content: 'Nội dung',
        fromEmail: 'Từ',
        toEmail: 'Tới',
        createTime: 'Tạo lúc',
        sendTime: 'Gửi lúc',
        openTime: 'Mở lúc',
        emailType: 'Loại email',
        userType: 'Email cá nhân',
        massType: 'Email marketing',
        emailSubject: 'Tiêu đề',
        emailDetail: 'Chi tiết email',
        allEmails: 'Tất cả',
        inboundEmail: 'Gửi đến',
        outboundEmail: 'Gửi đi',
        allStatuses: 'Tất cả trạng thái',
        failed: 'Thất bại',
      }
    }
  }
}
</script>

<style scoped>
.log-dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%; margin-right: 6px; vertical-align: middle; }
.log-list { display: flex; flex-direction: column; gap: 8px; }
.log-card {
  display: flex; align-items: flex-start; background: #fff;
  border: 1px solid #e8eaed; border-left: 4px solid #888; border-radius: 6px;
  padding: 12px 16px; cursor: pointer; transition: all 0.15s; gap: 16px;
}
.log-card:hover { border-color: #c9d1d9; box-shadow: 0 2px 8px rgba(0,0,0,0.06); transform: translateY(-1px); }
.log-card--error { background: #fef6f6; }

.log-card__time { min-width: 110px; flex-shrink: 0; }
.log-time-main { font-size: 13px; color: #333; font-weight: 500; }
.log-meta { font-size: 11px; color: #888; }

.log-card__type { min-width: 130px; flex-shrink: 0; }
.log-type-pill {
  display: inline-block; padding: 3px 10px; border-radius: 12px;
  color: #fff; font-size: 11px; font-weight: 600; letter-spacing: 0.3px; white-space: nowrap;
}

.log-card__main { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 4px; }
.log-row-1 { font-size: 14px; color: #222; }
.log-row-2 { font-size: 13px; color: #555; word-break: break-all; }
.log-row-3 { font-size: 12px; }

.log-card__status { display: flex; align-items: flex-start; padding-top: 2px; flex-shrink: 0; }
.log-card__chev { display: flex; align-items: center; color: #bbb; flex-shrink: 0; }

.log-meta-grid {
  display: grid; grid-template-columns: repeat(4, 1fr);
  gap: 12px; padding: 12px; background: #f8f9fa; border-radius: 6px;
}
.log-meta-cell--wide { grid-column: span 2; }
.log-meta-label { font-size: 11px; color: #888; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }

.log-section { margin-top: 20px; }
.log-section__label--big {
  font-size: 14px; color: #222; font-weight: 600;
  padding-bottom: 6px; border-bottom: 1px solid #e8eaed; margin-bottom: 8px;
}
.log-content-block {
  background: #f6f8fa; padding: 12px; border-radius: 4px;
  word-break: break-word; font-size: 14px; line-height: 1.6;
}
.log-html-content { background: #fff; border: 1px solid #e8eaed; }
.log-html-content >>> img { max-width: 100%; }
</style>
<style>.log-modal .modal-xl { max-width: 1100px; }</style>
